import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { BehaviorSubject, distinctUntilChanged, firstValueFrom, pairwise, filter, startWith } from 'rxjs';
import { Web3Service } from './web3.service';
import { IWallet, IUser, IRanking, IAddress } from '../interfaces/interfaces';
import { ModalController } from '@ionic/angular';
import { ProfileReminderModalComponent } from '../modals/profile-reminder-modal/profile-reminder-modal.component';
import { MsService } from './ms.service';
import { AuthServiceApp } from './auth.service';

interface AirdropStateEntry {
  pending: boolean;
  txHash?: string;
  error?: string;
  updatedAt: number;
}

type AirdropStateMap = Record<string, AirdropStateEntry>;

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private user: IUser | null = null;
  private _defaultWallet: string | undefined;
  public user$: BehaviorSubject<IUser | null> = new BehaviorSubject<IUser | null>(this.user);
  private wallets: IWallet[] = [];
  public wallets$: BehaviorSubject<IWallet[]> = new BehaviorSubject<IWallet[]>([]);
  private readonly AIRDROP_STATE_STORAGE_KEY = 'zerodevAirdropState';
  private airdropState: AirdropStateMap = {};


  constructor(
    private apiService: ApiService,
    private msService: MsService,
    private authService: AuthServiceApp
  ) {
    this.airdropState = this.loadStoredAirdropState();
    if (this.authService.isAuthenticated$.value) {
      this.getMe().catch(error => {
        console.error('[UserService] Error fetching initial user data', error);
      });
    }
    this.user$.pipe(distinctUntilChanged()).subscribe((user) => {
      if (user) {
        const wallets = user.wallets || [];
        this.defaultWallet = wallets.find((wallet: IWallet) => wallet.default)?.address || wallets[0]?.address;
      }
    });

    // Fetch user data and wallets when authentication state changes from false to true (runtime)
    this.authService.isAuthenticated$.pipe(
      startWith(this.authService.isAuthenticated$.value), // Ensure we have an initial value for pairwise
      pairwise(),
      filter(([previous, current]) => !previous && current)
    ).subscribe(async () => {
      try {
        await Promise.all([
          this.getMe(),
          this.getWallets()
        ]);
      } catch (error) {
        console.error('[UserService] Error fetching user data after authentication', error);
      }
    });
  }

  set defaultWallet(walletAddress: string) {
    this._defaultWallet = walletAddress;
  }

  get defaultWallet(): string | undefined {
    if (this._defaultWallet) return this._defaultWallet;
    if (localStorage.getItem('activeWallet')) return localStorage.getItem('activeWallet') as string;
    return;
  }

  async setDefaultWallet(walletAddress: string): Promise<any> {
    const setDefaultWalletRes = await firstValueFrom(this.apiService.put('users/default-wallet', { walletAddress }));
    return setDefaultWalletRes;
  }

  async createUser(username: string, metadata: { email?: string }, provider: string) {
    const payload = {
      userData: {
        username,
        wallets: [],
        metadata,
      },
      provider,
    };
    const createUserRequest = this.apiService.post('users', payload);
    return await firstValueFrom(createUserRequest);
  }

  async createCustodialWallet() {
    const walletData = {
      address: '0x0',
      provider: 'custodial',
      label: 'StarWallet',
    };
    const createWalletRequest = this.msService.post('web3-service/wallet', walletData);
    return await firstValueFrom(createWalletRequest);
  }

  async getMe(): Promise<IUser | null> {
    try {
      const getMeRequest = this.msService.get('auth-service/me');
      const getMeResponse: any = await firstValueFrom(getMeRequest);
      const requiredFields: (keyof IAddress)[] = ['postalCode'];
      const metadata = getMeResponse.metadata || {};
      const hadOldAddress = requiredFields.every((field) => {
        return metadata.address?.hasOwnProperty(field);
      });
      if (getMeResponse._id) {
        this.user = getMeResponse as IUser;
        const missingFields = [];
        if (!metadata.hasOwnProperty('firstName')) {
          missingFields.push('firstName');
        }
        if (!metadata.hasOwnProperty('lastName')) {
          missingFields.push('lastName');
        }
        if (!metadata.hasOwnProperty('email')) {
          missingFields.push('email');
        }
        if (!metadata.hasOwnProperty('phone')) {
          missingFields.push('phone');
        }
        if (!hadOldAddress) {
          missingFields.push('address');
        }

        if (missingFields.length > 0) {
          // const mode = missingFields.length === 4 ? 'easyPoints' : 'forgotSomething';
          // const modal = await this.modalCtrl.create({
          //   component: ProfileReminderModalComponent,
          //   cssClass: 'custom-profile-reminder-modal',
          //   componentProps: {
          //     mode,
          //   },
          // });
          // await modal.present();
        }

        this.user$.next(this.user);
        return this.user;
      }
      return null;
    } catch (error: any) {
      console.error('[UserService->getMe]', error.message);
      throw new Error('Failed to fetch user information');
    }
  }

  async getMyRankingPosition(ranking: IRanking[]): Promise<{ position: number; total: number }> {
    try {
      const position = ranking.findIndex((user: any) => user.userId == this.user?._id) + 1;
      return { position: position, total: ranking.length };
    } catch (error: any) {
      console.error('[UserService->getMyRankingPosition]', error.message);
      throw new Error('Failed to get user Position in rank');
    }
  }

  async getWallets(forceRefresh: boolean = true): Promise<IWallet[]> {
    if (!forceRefresh && this.wallets.length) {
      return [...this.wallets];
    }
    const getWalletsRequest = this.msService.get('web3-service/wallets');
    const getWalletsResponse: any = await firstValueFrom(getWalletsRequest);
    const rawWallets = this.extractWalletsFromResponse(getWalletsResponse);
    const decoratedWallets = this.decorateWallets(rawWallets);
    this.wallets = decoratedWallets;
    this.wallets$.next([...this.wallets]);
    return [...this.wallets];
  }

  getCachedWallets(): IWallet[] {
    return [...this.wallets];
  }

  getAirdropState(address: string): AirdropStateEntry | undefined {
    const key = this.normalizeAddress(address);
    if (!key) return undefined;
    return this.airdropState[key];
  }

  markAirdropPending(address: string) {
    this.upsertAirdropState(address, { pending: true });
  }

  markAirdropComplete(address: string, txHash?: string) {
    this.upsertAirdropState(address, { pending: false, txHash, error: undefined });
  }

  markAirdropError(address: string, error?: string) {
    this.upsertAirdropState(address, { pending: true, error: error || 'Unable to confirm airdrop' });
  }

  clearAirdropState(address: string) {
    const key = this.normalizeAddress(address);
    if (!key || !this.airdropState[key]) return;
    delete this.airdropState[key];
    this.persistAirdropState();
    this.applyWalletPatch(address, { pendingAirdrop: false, airdropError: null, airdropTxHash: null });
  }

  async getCustodialPrivateKey() {
    const getPkResponse = await firstValueFrom(this.apiService.get('users/private-key'));
    return getPkResponse;
  }

  private extractWalletsFromResponse(response: any): IWallet[] {
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response?.wallets)) {
      return response.wallets;
    }
    return [];
  }

  private decorateWallets(wallets: IWallet[]): IWallet[] {
    const withState = wallets.map((wallet) => {
      if (wallet.provider !== 'zerodev') return wallet;
      const key = this.normalizeAddress(wallet.address);
      if (!key) return wallet;
      const state = this.ensureAirdropStateEntry(key);
      return {
        ...wallet,
        pendingAirdrop: state.pending,
        airdropTxHash: state.txHash ?? null,
        airdropError: state.error ?? null,
      };
    });
    this.pruneStaleAirdropState(withState);
    return withState;
  }

  private ensureAirdropStateEntry(addressKey: string): AirdropStateEntry {
    if (!this.airdropState[addressKey]) {
      this.airdropState[addressKey] = { pending: true, updatedAt: Date.now() };
      this.persistAirdropState();
    }
    return this.airdropState[addressKey];
  }

  private pruneStaleAirdropState(wallets: IWallet[]) {
    const active = new Set(
      wallets
        .filter((wallet) => wallet.provider === 'zerodev')
        .map((wallet) => this.normalizeAddress(wallet.address))
        .filter((address): address is string => !!address),
    );
    let mutated = false;
    Object.keys(this.airdropState).forEach((key) => {
      if (!active.has(key)) {
        delete this.airdropState[key];
        mutated = true;
      }
    });
    if (mutated) {
      this.persistAirdropState();
    }
  }

  private normalizeAddress(address?: string): string | null {
    return address ? address.toLowerCase() : null;
  }

  private loadStoredAirdropState(): AirdropStateMap {
    try {
      const stored = localStorage.getItem(this.AIRDROP_STATE_STORAGE_KEY);
      return stored ? (JSON.parse(stored) as AirdropStateMap) : {};
    } catch (error) {
      console.error('[UserService->loadStoredAirdropState]', error);
      return {};
    }
  }

  private persistAirdropState() {
    try {
      localStorage.setItem(this.AIRDROP_STATE_STORAGE_KEY, JSON.stringify(this.airdropState));
    } catch (error) {
      console.error('[UserService->persistAirdropState]', error);
    }
  }

  private applyWalletPatch(address: string, patch: Partial<IWallet>) {
    if (!this.wallets.length) return;
    const normalized = this.normalizeAddress(address);
    if (!normalized) return;
    let mutated = false;
    this.wallets = this.wallets.map((wallet) => {
      if (this.normalizeAddress(wallet.address) !== normalized) return wallet;
      mutated = true;
      return { ...wallet, ...patch };
    });
    if (mutated) {
      this.wallets$.next([...this.wallets]);
    }
  }

  private upsertAirdropState(address: string, patch: Partial<AirdropStateEntry>) {
    const key = this.normalizeAddress(address);
    if (!key) return;
    const current = this.airdropState[key] || { pending: true, updatedAt: Date.now() };
    const next: AirdropStateEntry = { ...current, ...patch, updatedAt: Date.now() };
    if ('error' in patch && patch.error === undefined) {
      delete next.error;
    }
    if ('txHash' in patch && patch.txHash === undefined) {
      delete next.txHash;
    }
    this.airdropState[key] = next;
    this.persistAirdropState();
    this.applyWalletPatch(address, {
      pendingAirdrop: next.pending,
      airdropTxHash: next.txHash ?? null,
      airdropError: next.error ?? null,
    });
  }
}
